import { CopyButton } from "./CopyButton";
import { DualScrollArea } from "./DualScrollArea";
import { PlatformIcon } from "./PlatformIcon";
import { SourceGroupsCell } from "./SourceGroupsCell";
import { TargetKindBadge } from "./TargetKindBadge";
// src/features/links/LinksTable.tsx
// IR-1047 / IR-1238 — Join Links workspace table.
// A single ordered COLUMNS list (each entry carrying a render(row, ctx) fn)
// drives BOTH the header and the body, so column order and show/hide are
// defined in one place. Column order is Tomer's IR-1238 mandatory sequence:
//   Link · Name · Type · Picture · Platform · Operational Status · Actions ·
//   Approval Required · Classification · Bio · Members · [Source Groups ·
//   Risk Score · First Seen · Last Seen · Appearances · Avatar (finder)]
// The trailing six are optional and default-hidden (see column-visibility.ts);
// Link + Actions are always visible. Visibility is controlled by `visibleCols`.
//
// Target-group tint (TARGET_GROUP_TINT + data-col-group="target") marks the
// columns that describe the TARGET group. Post-IR-1238 the mandatory order
// separates them, so the tint is kept only on the contiguous Link·Name·Type·
// Picture band (IR-1386 r3 moved Type inside it) and dropped from the
// now-isolated Bio/Members.
//
// A row click opens the target-group detail panel (see LinkDetailPanel); cells
// flagged `interactive` stop click/key propagation so their controls keep
// working without opening the panel. Operational Status + Approval Required are
// inline-editable (fire onUpdateLink). Sortable columns render a clickable
// A-Z/Z-A sort header wired to onSortChange.
import type { LinkUpdatePayload } from "./api";
import { DEFAULT_VISIBLE } from "./column-visibility";
import { ApprovalRequiredCell, DismissButton, OperationalStatusCell } from "./editCells";
import { highlightMatches, snippetAroundMatch } from "./highlight";
import { linkUrl } from "./linkUrl";
import {
  FetchPreviewButton,
  PreviewBioCell,
  PreviewMembersCell,
  PreviewNameCell,
  PreviewPictureCell,
} from "./previewCells";
import type { LinkOnboardingRow } from "./types";

export type SortDir = "asc" | "desc";

const TARGET_GROUP_TINT = "bg-[var(--iris-bg-2)]";

// IR-1233 r4: keep the header visible while the DualScrollArea bottom pane
// scrolls vertically (match nav jumps rows off-screen otherwise). The bg must
// be OPAQUE (the .iris-table thead th base style is transparent) so scrolled
// rows never bleed through; bg-2 is the card background, so tinted target
// headers keep their look. z-10 stays above interactive cell content.
const STICKY_TH = "sticky top-0 z-10 bg-[var(--iris-bg-2)]";

// Columns the picker can never hide.
const ALWAYS_VISIBLE = new Set<string>(["link", "actions"]);

// IR-1233 r6: the Link cell is `max-w-[28ch] truncate`, so a match in the TAIL
// of a long URL (https://t.me/+ plus a 22-char invite hash = 36 chars) rendered
// a <mark> that was clipped off-screen — useMatchNav counted it and scrolled to
// it with nothing visible to land on. Re-center the same way the Bio cell has
// since r4. Conservative vs the 28ch clamp: the ellipsis plus the lead-in put
// the match around column 9, comfortably inside.
const LINK_MATCH_VISIBLE_WINDOW = 24;
const LINK_SNIPPET_LEAD_IN = 8;

interface Props {
  rows: LinkOnboardingRow[];
  sort: string;
  dir: SortDir;
  onSortChange: (column: string, dir: SortDir) => void;
  onUpdateLink: (id: number, patch: LinkUpdatePayload) => void;
  onDismiss: (id: number) => void;
  onResolvePreview: (id: number) => void;
  resolvingPreviewId?: number | null;
  onRowClick?: (id: number) => void;
  // IR-1233 r3 — active search query; occurrences in the Link/Name/Bio/Source
  // Group cells are wrapped in <mark data-search-match> (see ./highlight).
  highlightQuery?: string | undefined;
  // IR-1238 — keys of the toggleable columns to show (from column-visibility).
  // Link + Actions are always shown regardless. Defaults to DEFAULT_VISIBLE.
  visibleCols?: string[];
}

// Handlers + context threaded into each column's render().
interface CellContext {
  onUpdateLink: (id: number, patch: LinkUpdatePayload) => void;
  onDismiss: (id: number) => void;
  onResolvePreview: (id: number) => void;
  resolvingPreviewId?: number | null | undefined;
  highlightQuery?: string | undefined;
}

type ColGroup = "target";
interface ColumnDef {
  key: string;
  label: string;
  align?: "right";
  // false → plain <th> (no sort button); otherwise a sortable header.
  sortable?: boolean;
  // "target" → background tint + data-col-group="target".
  group?: ColGroup;
  // true → the <td> stops click/key propagation so inline controls keep working
  // without opening the row detail panel.
  interactive?: boolean;
  // Extra <td> classes (e.g. tabular-nums).
  cellClassName?: string;
  // IR-1213: optional width clamp (Tailwind class, e.g. "w-16") applied to BOTH
  // the <th> and <td> so wide columns (Picture, Operational Status) stop
  // dominating the table and long headers wrap to two lines.
  width?: string;
  render: (row: LinkOnboardingRow, ctx: CellContext) => React.ReactNode;
}

function emDash() {
  return <span className="text-[var(--iris-fg-4)]">—</span>;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

// Canonical column order (IR-1238 mandatory sequence, then optional block).
const COLUMNS: ColumnDef[] = [
  {
    key: "link",
    label: "Link",
    group: "target",
    interactive: true,
    render: (row, ctx) => (
      <>
        <div className="flex items-center gap-1">
          <a
            href={linkUrl(row)}
            target="_blank"
            rel="noopener noreferrer"
            className="block max-w-[28ch] truncate font-mono text-xs underline decoration-dotted underline-offset-2 hover:text-[var(--iris-fg-1)]"
            title={row.link}
          >
            {highlightMatches(
              snippetAroundMatch(linkUrl(row), ctx.highlightQuery, {
                visibleWindow: LINK_MATCH_VISIBLE_WINDOW,
                leadIn: LINK_SNIPPET_LEAD_IN,
              }),
              ctx.highlightQuery,
            )}
          </a>
          <CopyButton text={linkUrl(row)} />
        </div>
        {row.onboarding_source === "manual" && (
          <span
            className="mt-0.5 inline-block rounded-full bg-[var(--iris-bg-chip)] px-1.5 py-0.5 text-[10px] text-[var(--iris-fg-3)]"
            title="Manually added"
          >
            Manual
          </span>
        )}
      </>
    ),
  },
  {
    key: "target_name",
    label: "Name",
    sortable: false,
    group: "target",
    // IR-1386 r4 (Rotem): "you created an unneeded gap which makes it hard to
    // use the table - we dont need all this space". Name was the only wide
    // column with NO clamp, so once r3 capped Actions at w-20 and moved Type in
    // beside Picture, every pixel of slack the auto layout had to give away
    // landed here — a ~400px column holding a 20-character group name, with the
    // Type badge pushed off to the right of a blank stripe.
    width: "w-56",
    render: (row, ctx) => (
      <PreviewNameCell preview={row.target_preview} highlightQuery={ctx.highlightQuery} />
    ),
  },
  {
    // IR-1386 r3 (Rotem): "change target to 'type'" + "move 'Type' in the table
    // - before the 'picture'". What the link points AT is the first thing an
    // analyst triages on, so it now precedes the preview columns instead of
    // trailing them.
    key: "target_kind",
    label: "Type",
    // Joins the target tint band: it now sits INSIDE Link·Name·…·Picture, and a
    // column without the tint there would punch a visible gap through a band
    // that is meant to read as one contiguous "about the target" group.
    group: "target",
    // IR-1386 r4: wide enough for "Private channel" on ONE line. Squeezed, the
    // badge wrapped to two lines and set the height of every row in the table.
    width: "w-28",
    render: (row) => <TargetKindBadge kind={row.target_kind} />,
  },
  {
    key: "target_picture",
    label: "Picture",
    sortable: false,
    group: "target",
    width: "w-16",
    render: (row) => <PreviewPictureCell preview={row.target_preview} />,
  },
  {
    key: "platform",
    label: "Platform",
    width: "w-16",
    render: (row) => <PlatformIcon platform={row.platform} />,
  },
  {
    key: "operational_status",
    label: "Operational Status",
    interactive: true,
    width: "w-36",
    render: (row, ctx) => (
      <OperationalStatusCell
        value={row.operational_status}
        onChange={(next) => ctx.onUpdateLink(row.id, { operational_status: next })}
      />
    ),
  },
  {
    key: "actions",
    label: "Actions",
    sortable: false,
    interactive: true,
    // IR-1386 r3 (Rotem): icon-only here, sized to the platform glyph. `w-20`
    // caps what used to be the table's widest column at the two 24px buttons it
    // actually needs.
    width: "w-20",
    render: (row, ctx) => (
      <div className="flex items-center gap-1">
        <FetchPreviewButton
          compact
          preview={row.target_preview}
          pending={ctx.resolvingPreviewId === row.id}
          onFetch={() => ctx.onResolvePreview(row.id)}
        />
        <DismissButton compact onDismiss={() => ctx.onDismiss(row.id)} />
      </div>
    ),
  },
  {
    key: "approval_required",
    label: "Approval Required",
    interactive: true,
    render: (row, ctx) => (
      <ApprovalRequiredCell
        value={row.approval_required}
        onChange={(next) => ctx.onUpdateLink(row.id, { approval_required: next })}
      />
    ),
  },
  {
    key: "classification",
    label: "Classification",
    render: (row) =>
      row.classification && row.classification.length > 0 ? (
        <span className="text-xs">{row.classification.join(", ")}</span>
      ) : (
        emDash()
      ),
  },
  {
    key: "target_bio",
    label: "Bio",
    sortable: false,
    render: (row, ctx) => (
      <PreviewBioCell preview={row.target_preview} highlightQuery={ctx.highlightQuery} />
    ),
  },
  {
    key: "target_members",
    label: "Members",
    align: "right",
    sortable: false,
    render: (row) => <PreviewMembersCell preview={row.target_preview} />,
  },
  {
    key: "source_group_count",
    label: "Source Groups",
    render: (row, ctx) => (
      <SourceGroupsCell groups={row.source_groups} highlightQuery={ctx.highlightQuery} />
    ),
  },
  {
    key: "risk_score",
    label: "Risk Score",
    align: "right",
    cellClassName: "tabular-nums",
    render: (row) => (row.risk_score != null ? row.risk_score.toFixed(2) : emDash()),
  },
  {
    key: "first_seen",
    label: "First Seen",
    render: (row) => (
      <span className="text-xs text-[var(--iris-fg-3)]">{formatDate(row.first_seen_at)}</span>
    ),
  },
  {
    key: "last_seen",
    label: "Last Seen",
    render: (row) => (
      <span className="text-xs text-[var(--iris-fg-3)]">{formatDate(row.last_seen_at)}</span>
    ),
  },
  {
    key: "appearances",
    label: "Appearances",
    render: (row) => row.appearances,
  },
  {
    key: "avatar_finder",
    label: "Avatar (finder)",
    render: (row) =>
      row.avatar_finder ? (
        <span className="text-xs">
          {row.avatar_finder.name}
          <span className="ml-1 text-[var(--iris-fg-4)]">({row.avatar_finder.platform})</span>
        </span>
      ) : (
        emDash()
      ),
  },
];

interface SortHeaderProps {
  column: string;
  label: string;
  sort: string;
  dir: SortDir;
  onSort: (column: string, dir: SortDir) => void;
  align?: "right" | undefined;
  className?: string | undefined;
  group?: ColGroup | undefined;
}

function SortHeader({
  column,
  label,
  sort,
  dir,
  onSort,
  align,
  className,
  group,
}: SortHeaderProps) {
  const active = sort === column;
  const nextDir: SortDir = active && dir === "asc" ? "desc" : "asc";
  const indicator = active ? (dir === "asc" ? "▲" : "▼") : "";
  return (
    <th
      className={
        [align === "right" ? "text-right" : "", className ?? ""].join(" ").trim() || undefined
      }
      data-col-group={group}
    >
      <button
        type="button"
        aria-label={`Sort by ${label}`}
        aria-pressed={active}
        data-active={active}
        className="inline-flex items-center gap-1 text-inherit hover:text-[var(--iris-fg-1)]"
        onClick={() => onSort(column, nextDir)}
      >
        <span>{label}</span>
        {indicator && <span className="text-[var(--iris-fg-4)]">{indicator}</span>}
      </button>
    </th>
  );
}

// Stop a control interaction from bubbling to the row's open-panel handler.
function stopClick(e: React.MouseEvent) {
  e.stopPropagation();
}
function stopKeys(e: React.KeyboardEvent) {
  if (e.key === "Enter" || e.key === " ") e.stopPropagation();
}

export function LinksTable({
  rows,
  sort,
  dir,
  onSortChange,
  onUpdateLink,
  onDismiss,
  onResolvePreview,
  resolvingPreviewId,
  onRowClick,
  highlightQuery,
  visibleCols = DEFAULT_VISIBLE,
}: Props) {
  if (rows.length === 0) {
    return (
      <div className="iris-card p-6 text-center text-sm text-[var(--iris-fg-3)]">
        No links match the current filters.
      </div>
    );
  }

  const visibleSet = new Set(visibleCols);
  const cols = COLUMNS.filter((c) => ALWAYS_VISIBLE.has(c.key) || visibleSet.has(c.key));
  const ctx: CellContext = {
    onUpdateLink,
    onDismiss,
    onResolvePreview,
    resolvingPreviewId,
    highlightQuery,
  };

  return (
    <div className="iris-card">
      <DualScrollArea>
        {/* IR-1386 r4 (Rotem): `--dense` trims 4px of vertical padding per row
            — with the Type badge no longer wrapping to two lines, that is about
            two extra rows per screen on the table she called hard to use. */}
        <table className="iris-table iris-table--dense">
          <thead>
            <tr>
              {cols.map((c) => {
                const tint = c.group === "target" ? TARGET_GROUP_TINT : undefined;
                return c.sortable === false ? (
                  <th
                    key={c.key}
                    className={
                      [
                        STICKY_TH,
                        c.align === "right" ? "text-right" : "",
                        tint ?? "",
                        c.width ?? "",
                      ]
                        .join(" ")
                        .trim() || undefined
                    }
                    data-col-group={c.group}
                  >
                    {c.label}
                  </th>
                ) : (
                  <SortHeader
                    key={c.key}
                    column={c.key}
                    label={c.label}
                    sort={sort}
                    dir={dir}
                    onSort={onSortChange}
                    align={c.align}
                    className={[STICKY_TH, tint ?? "", c.width ?? ""].join(" ").trim() || undefined}
                    group={c.group}
                  />
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                // IR-1233 r6 — match-navigation registry. Every row the backend
                // returns while searching IS a match, so the rows are the
                // registry; useMatchNav walks these in document order.
                data-match-row={highlightQuery?.trim() ? "" : undefined}
                className={onRowClick ? "cursor-pointer" : undefined}
                onClick={onRowClick ? () => onRowClick(row.id) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(row.id);
                        }
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? "button" : undefined}
              >
                {cols.map((c) => {
                  const tint = c.group === "target" ? TARGET_GROUP_TINT : undefined;
                  const className =
                    [
                      c.align === "right" ? "text-right" : "",
                      tint ?? "",
                      c.cellClassName ?? "",
                      c.width ?? "",
                    ]
                      .join(" ")
                      .trim() || undefined;
                  return (
                    <td
                      key={c.key}
                      className={className}
                      data-col-group={c.group}
                      onClick={c.interactive ? stopClick : undefined}
                      onKeyDown={c.interactive ? stopKeys : undefined}
                    >
                      {c.render(row, ctx)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </DualScrollArea>
    </div>
  );
}
